import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { ReactNode } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import SvgCardRequestLabel from 'src/components/Svgs/SvgCardRequestLabel'
import SvgCheck from 'src/components/Svgs/SvgCheck'
import SvgClockRewind from 'src/components/Svgs/SvgClockRewind'
import SvgPeopleSingle from 'src/components/Svgs/SvgPeopleSingle'
import { useTranslate } from 'src/localization/Trns'
import { css } from 'styled-components'

interface BenefitTile {
  id: 'bulk' | 'keepExisting' | 'syncAutomatically' | 'assignPeople'
  icon: React.ReactElement
  title: string
  description: string
}

interface ConnectCardFeedViewProps {
  title: string
  subtitle: ReactNode
  toggleSlot?: ReactNode
  onGetStarted: () => void
  onCancel: () => void
}

const ConnectCardFeedView: React.FC<ConnectCardFeedViewProps> = ({
  title,
  subtitle,
  toggleSlot,
  onGetStarted,
  onCancel,
}): React.ReactElement => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()

  const handleGetStarted = (): void => {
    analytics.track('add company card - get started', { tab: 'card_feed' })
    onGetStarted()
  }

  const iconStyle = css`
    flex-shrink: 0;
    width: 24px;
    height: 24px;
  `

  const tiles: BenefitTile[] = [
    {
      id: 'bulk',
      icon: <SvgCardRequestLabel css={iconStyle} />,
      title: translate('Enroll in bulk'),
      description: translate('Bring your entire card program over at once'),
    },
    {
      id: 'keepExisting',
      icon: <SvgCheck css={iconStyle} fill="var(--color-cta)" />,
      title: translate('Keep your existing cards'),
      description: translate('Works with your Visa or Mastercard cards'),
    },
    {
      id: 'syncAutomatically',
      icon: (
        <SvgClockRewind
          css={iconStyle}
          width={24}
          height={24}
          viewBox="-4 -5 24 24"
        />
      ),
      title: translate('Stay in sync automatically'),
      description: translate('New cards from your bank appear in your feed'),
    },
    {
      id: 'assignPeople',
      icon: (
        <SvgPeopleSingle
          css={iconStyle}
          fill="var(--color-cta)"
          stroke="var(--color-cta)"
        />
      ),
      title: translate('Assign to the right people'),
      description: translate('Review and assign each card to a teammate'),
    },
  ]

  return (
    <div
      data-testid="connect-card-feed-view"
      css={css`
        display: flex;
        flex-direction: column;
        padding: 60px 60px 40px 60px;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          max-width: 440px;
        `}
      >
        <span>{title}</span>
      </div>
      <div
        css={css`
          margin-top: 30px;
          max-width: 500px;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            max-width: none;
          `)}
          margin-bottom: 20px;
        `}
      >
        {subtitle}
      </div>
      {toggleSlot}
      <div
        css={css`
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        {translate('Connect Your Card Feed')}
      </div>
      <p
        css={css`
          margin-top: 12px;
          margin-bottom: 24px;
          ${getFontCss(
            14,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate(
          'Bring your full corporate card program into Extend through your issuing bank. Once connected, eligible cards are enrolled automatically. Supports Mastercard and Visa.',
        )}
      </p>
      <ul
        css={css`
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px 24px;
          list-style: none;
          padding: 0;
          margin: 0 0 40px 0;

          ${getBreakpointDown(breakpointSizes.XSM)(css`
            grid-template-columns: 1fr;
          `)}
        `}
      >
        {tiles.map((tile) => (
          <li
            key={tile.id}
            css={css`
              display: flex;
              flex-direction: row;
              gap: 12px;
              align-items: flex-start;
            `}
          >
            <span
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                border-radius: 8px;
                background-color: ${styleConstants.colors.gray_light};
                flex-shrink: 0;
              `}
            >
              {tile.icon}
            </span>
            <span
              css={css`
                display: flex;
                flex-direction: column;
                gap: 4px;
              `}
            >
              <span
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {tile.title}
              </span>
              <span
                css={css`
                  ${getFontCss(
                    12,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {tile.description}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <div
        css={css`
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
        `}
      >
        {/*
          Cancel is hand-rolled rather than using the design-system Button because
          none of the existing ButtonDesigns variants (designCta / designCtaInverse
          / designRed / plainNoShadow / designUtilityToolbar) match the bare text
          treatment in the Figma. plainNoShadow has a filled grey background, not
          a transparent text link. If a text/tertiary Button variant lands later,
          swap this for it so brand theming flows through.
        */}
        <button
          type="button"
          onClick={onCancel}
          data-testid="connect-card-feed-cancel"
          css={css`
            background: none;
            border: none;
            padding: 0;
            cursor: pointer;
            ${getFontCss(
              14,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.medium,
            )};

            &:focus-visible {
              outline: 2px solid var(--color-cta);
              outline-offset: 2px;
              border-radius: 2px;
            }
          `}
        >
          {translate('Cancel')}
        </button>
        <Button
          data-testid="connect-card-feed-get-started"
          type={ButtonTypes.button}
          design={ButtonDesigns.designCta}
          text={translate('Get Started')}
          onClick={handleGetStarted}
          big
        />
      </div>
    </div>
  )
}

export default ConnectCardFeedView
